import { useEffect, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ThemeProvider } from "@mui/material/styles";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { LoadingState, ErrorState } from "@/components/feedback/states";
import { useToast } from "@/components/feedback/toaster";
import { Form } from "@/components/form/form";
import {
  FormColorField,
  FormImageField,
  FormSwitch,
  FormTextField,
} from "@/components/form/fields";
import { buildTheme } from "@/theme/create-theme";
import { useBranding, useUpdateBranding } from "@/api/settings";

export const Route = createFileRoute("/_app/settings/branding")({
  component: BrandingSettings,
});

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const schema = z.object({
  companyName: z.string().optional(),
  logoUrl: z.string().optional(),
  logoData: z
    .string()
    .refine((v) => v === "" || v.startsWith("data:image/"), "Must be an uploaded image")
    .optional(),
  primaryColor: z.string().regex(HEX, "Hex colour, e.g. #4f46e5").or(z.literal("")).optional(),
  accentColor: z.string().regex(HEX, "Hex colour").or(z.literal("")).optional(),
  supportEmail: z.string().email().or(z.literal("")).optional(),
  footerText: z.string().optional(),
  hideUdipBrand: z.boolean().optional(),
});
type Values = z.infer<typeof schema>;

function BrandingSettings() {
  const toast = useToast();
  const { data, isLoading, error, refetch } = useBranding();
  const update = useUpdateBranding();

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      companyName: "",
      logoUrl: "",
      logoData: "",
      primaryColor: "",
      accentColor: "",
      supportEmail: "",
      footerText: "",
      hideUdipBrand: false,
    },
  });
  const { reset } = form;

  useEffect(() => {
    if (data) {
      reset({
        companyName: data.companyName ?? "",
        logoUrl: data.logoUrl ?? "",
        logoData: data.logoData ?? "",
        primaryColor: data.primaryColor ?? "",
        accentColor: data.accentColor ?? "",
        supportEmail: data.supportEmail ?? "",
        footerText: data.footerText ?? "",
        hideUdipBrand: data.hideUdipBrand ?? false,
      });
    }
  }, [data, reset]);

  const watched = useWatch({ control: form.control });
  const primaryColor = watched.primaryColor ?? "";
  const accentColor = watched.accentColor ?? "";
  const previewTheme = useMemo(
    () =>
      buildTheme({
        primaryColor: HEX.test(primaryColor) ? primaryColor : null,
        accentColor: HEX.test(accentColor) ? accentColor : null,
      }),
    [primaryColor, accentColor],
  );

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={() => refetch()} />;

  async function onSave(values: Values) {
    await update.mutateAsync(values);
    toast.success("Branding saved");
  }

  return (
    <PageContainer>
      <PageHeader
        title="Branding"
        description="Customise the console for your tenant — logo, colours and support links."
      />
      <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: { xs: "1fr", md: "3fr 2fr" } }}>
        <Card>
          <CardContent>
            <Form form={form} onSubmit={onSave}>
              <Stack spacing={2}>
                <FormTextField control={form.control} name="companyName" label="Company name" />
                <FormImageField
                  control={form.control}
                  name="logoData"
                  label="Logo"
                  maxKB={1024}
                  helperText="Shown in the top bar. PNG, JPG, SVG or WebP · under 1 MB."
                />
                <Stack direction="row" spacing={2}>
                  <FormColorField
                    control={form.control}
                    name="primaryColor"
                    label="Primary colour"
                    placeholder="#4f46e5"
                  />
                  <FormColorField
                    control={form.control}
                    name="accentColor"
                    label="Accent colour"
                    placeholder="#7c3aed"
                  />
                </Stack>
                <FormTextField control={form.control} name="supportEmail" label="Support email" />
                <FormTextField
                  control={form.control}
                  name="footerText"
                  label="Footer text"
                  multiline
                />
                <FormSwitch
                  control={form.control}
                  name="hideUdipBrand"
                  label="Hide “Powered by UDIP” in the footer"
                />
                <Box>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={form.formState.isSubmitting || !form.formState.isDirty}
                  >
                    Save changes
                  </Button>
                </Box>
              </Stack>
            </Form>
          </CardContent>
        </Card>

        <ThemeProvider theme={previewTheme}>
          <Card sx={{ bgcolor: "background.default" }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Preview
              </Typography>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                  {watched.logoData || watched.logoUrl ? (
                    <Box
                      component="img"
                      src={watched.logoData || watched.logoUrl}
                      alt=""
                      sx={{ height: 28, width: "auto" }}
                    />
                  ) : null}
                  <Typography variant="h6">{watched.companyName || "Your company"}</Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained">Primary</Button>
                  <Button variant="outlined" color="secondary">
                    Accent
                  </Button>
                </Stack>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2">Sample card</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Content adopts the branded palette across the console.
                    </Typography>
                  </CardContent>
                </Card>
                <Typography variant="caption" color="text.secondary">
                  {watched.footerText || "Footer text appears here."}
                  {watched.hideUdipBrand ? "" : " · Powered by UDIP"}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </ThemeProvider>
      </Box>
    </PageContainer>
  );
}
